/**
 * Web-backed refresh lock for the shared `HttpClient` (BUG15).
 *
 * The shared package is CP1 / DOM-free, so it can't touch `navigator`. This
 * module — which lives in apps/web where browser globals are allowed —
 * supplies a `RefreshLock` backed by the Web Locks API.
 *
 * `navigator.locks.request(name, fn)` runs `fn` while exclusively holding the
 * named lock and queues other requesters until it resolves. Because the lock
 * is scoped to the origin, it serializes the token refresh across BOTH
 * `HttpClient` instances in a tab (the app adapter + the sync engine's client)
 * AND across every tab of the same origin — which is exactly the cross-instance
 * + cross-tab rotating-refresh race BUG15 describes.
 *
 * Both `new HttpClient(...)` sites pass `makeWebRefreshLock()` with the one
 * shared `AUTH_REFRESH_LOCK_NAME` so they queue on the same lock.
 *
 * Fallback: if `navigator.locks` is unavailable (older browsers, non-secure
 * contexts, some embedded webviews), we degrade to a pass-through that runs
 * `fn` immediately. That restores the pre-BUG15 single-shot-refresh behavior
 * for those clients — the instance-level `refreshPromise` dedup still applies,
 * so it's no worse than before; only the cross-instance/cross-tab
 * serialization is lost.
 */

import type { RefreshLock } from '@shopista/shared/api';

interface LockManagerLike {
  request<T>(name: string, fn: () => Promise<T>): Promise<T>;
}

function getLockManager(): LockManagerLike | null {
  const nav = (globalThis as { navigator?: { locks?: LockManagerLike } }).navigator;
  if (nav && nav.locks && typeof nav.locks.request === 'function') {
    return nav.locks;
  }
  return null;
}

/**
 * Build a `RefreshLock` for injection into `HttpClient`. Uses `navigator.locks`
 * when available; otherwise a pass-through.
 */
export function makeWebRefreshLock(): RefreshLock {
  const locks = getLockManager();
  if (!locks) {
    return (_name, fn) => fn();
  }
  return (name, fn) => locks.request(name, fn);
}
